import {gql} from '@apollo/client';
import assert from 'assert';
import React from 'react';

import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {accessibleObjs} from 'graphql-client/InaccessibleInfo';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import getTestProps from 'test/lib/cypress/getTestProps';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import APIUtil from 'util/APIUtil';

import {
  EntityTestOrdersQuery,
  EntityTestOrdersQueryVariables,
  EntityTestOrdersQuery_folderItem_gXfeNGqcwZcyZxupf5lxs2BhfOt79q5hxbUtmds1TE as FolderItemWithoutTestOrders,
  EntityTestOrdersQuery_folderItem_BfwQBLloZdXV49uihO4VvOyY5VuCLpdwV8OdzR9L0 as StudyableEntityWithTestOrders,
  EntityTestOrdersQuery_folderItem_KMSPGe2MUKynzHCJ6jH7GgrLM6ftNsXKtVq7JBkSsQ as NonStudyableEntity,
} from './__generated/EntityTestOrdersQuery';
import {TEST_ORDERS_VIEW_TABLE_TEST_ORDER_FRAGMENT, TestOrdersViewTable} from './TestOrdersViewTable';

export const ENTITY_TEST_ORDERS_QUERY = gql`
  query EntityTestOrdersQuery($fileId: StrictString!) @skipAudit @engComponent(name: SAMPLE_TESTS) {
    folderItem(id: $fileId) {
      ... on RegistryItemInterface {
        id
        ...StudyableTypedValueDataGraphQL
        testOrders {
          ...TestOrdersViewTableTestOrderFragment
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('StudyableTypedValueData').fragment}
  ${TEST_ORDERS_VIEW_TABLE_TEST_ORDER_FRAGMENT}
`;

interface Props {
  entityId: string;
}

function isStudyableEntityWithTestOders(
  accessibleFolderItem: FolderItemWithoutTestOrders | StudyableEntityWithTestOrders | NonStudyableEntity
): accessibleFolderItem is StudyableEntityWithTestOrders {
  return accessibleFolderItem.hasOwnProperty('studies') && accessibleFolderItem.hasOwnProperty('testOrders');
}

export default function EntityTestOrdersView(props: Props): JSX.Element {
  assert(APIUtil.isBiologicalEntity(props.entityId), 'Expected an entity ID');
  return (
    <div
      className="react-tab-container"
      {...getTestProps({component: 'EntityTestOrdersView', element: 'root'})}
    >
      <BenchlingQuery<EntityTestOrdersQuery, EntityTestOrdersQueryVariables>
        query={ENTITY_TEST_ORDERS_QUERY}
        variables={{fileId: props.entityId}}
        renderLoading={() => <CenteredHorizontalThrobber />}
      >
        {(data) => {
          if (isInaccessible(data.folderItem) || !isStudyableEntityWithTestOders(data.folderItem)) {
            // If this is a read-only share link, the full folder item will be inaccessible and we don't want
            // to render a test orders table at all
            // We also don't render a tab for folder items that aren't studyable or don't have test orders
            return null;
          }

          const studyIds = accessibleObjs(data.folderItem.studies).map((study) => study.id);

          return (
            <TestOrdersViewTable
              testOrders={data.folderItem.testOrders}
              itemId={props.entityId}
              studyIds={studyIds}
              allowCreateTestOrders={true}
            />
          );
        }}
      </BenchlingQuery>
    </div>
  );
}
